import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Pencil, PiggyBank, Plus, Trash2 } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function BudgetsPage() {
  const { t, fmt } = useI18n();
  const { canEdit } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ category: "", monthly_limit: "" });
  const [error, setError] = useState("");

  const load = () => api.get("/budgets").then((r) => setRows(r.data));
  useEffect(() => { load().catch(() => {}); }, []);

  const over = useMemo(() => rows.filter((b) => b.over), [rows]);
  const usedCats = useMemo(() => rows.map((b) => b.category), [rows]);
  const availCats = t.catExpense.filter((c) => editing ? c === editing.category || !usedCats.includes(c) : !usedCats.includes(c));

  const openNew = () => { setEditing(null); setForm({ category: "", monthly_limit: "" }); setError(""); setOpen(true); };
  const openEdit = (b) => { setEditing(b); setForm({ category: b.category, monthly_limit: b.monthly_limit }); setError(""); setOpen(true); };

  const save = async () => {
    const payload = { category: form.category, monthly_limit: parseFloat(form.monthly_limit) };
    try {
      if (editing) await api.put(`/budgets/${editing.id}`, payload);
      else await api.post("/budgets", payload);
      setOpen(false);
      load().catch(() => {});
    } catch (e) {
      setError(fmtErr(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t.app.confirmDelete)) return;
    await api.delete(`/budgets/${id}`);
    load().catch(() => {});
  };

  return (
    <div className="space-y-5" data-testid="budgets-page">
      {over.length > 0 && (
        <div className="flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-5 py-4" data-testid="budgets-over-banner">
          <AlertTriangle className="h-5 w-5 shrink-0 text-red-600" />
          <p className="text-sm text-red-700">
            <span className="font-semibold">{t.budget.overBudget}:</span>{" "}
            {over.map((b) => b.category).join(", ")}
          </p>
        </div>
      )}

      <div className="flex justify-end">
        {canEdit && (
          <Button onClick={openNew} data-testid="budget-new-button">
            <Plus className="mr-2 h-4 w-4" />{t.budget.new}
          </Button>
        )}
      </div>

      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
        {rows.length === 0 && (
          <p className="col-span-full rounded-2xl border border-dashed border-slate-200 py-16 text-center text-sm text-slate-400">{t.budget.empty}</p>
        )}
        {rows.map((b) => {
          const barColor = b.over ? "bg-negative" : b.pct >= 80 ? "bg-amber-500" : "bg-brand";
          const status = b.over ? t.budget.overBudget : b.pct >= 80 ? t.budget.nearLimit : t.budget.onTrack;
          const statusCls = b.over ? "bg-red-50 text-red-600" : b.pct >= 80 ? "bg-amber-50 text-amber-600" : "bg-emerald-50 text-emerald-600";
          return (
            <div key={b.id} data-testid={`budget-card-${b.id}`}
              className="group flex flex-col rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm transition-transform duration-200 hover:-translate-y-0.5">
              <div className="flex items-start justify-between">
                <div className="grid h-11 w-11 place-items-center rounded-xl bg-slate-900">
                  <PiggyBank className="h-5 w-5 text-white" />
                </div>
                {canEdit && (
                  <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <button onClick={() => openEdit(b)} aria-label={t.app.edit} data-testid={`budget-edit-${b.id}`}
                      className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-900">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => remove(b.id)} aria-label={t.app.delete} data-testid={`budget-delete-${b.id}`}
                      className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
              <div className="mt-5 flex items-center justify-between">
                <p className="font-display text-base font-semibold tracking-tight text-slate-900">{b.category}</p>
                <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${statusCls}`} data-testid={`budget-status-${b.id}`}>{status}</span>
              </div>
              <p className="mt-3">
                <span className={`tnum font-display text-2xl font-semibold tracking-tight ${b.over ? "text-negative" : "text-slate-900"}`}>{fmt(b.spent)}</span>
                <span className="tnum font-mono text-xs text-slate-400"> / {fmt(b.monthly_limit)}</span>
              </p>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
                <div className={`h-full rounded-full ${barColor} transition-all`} style={{ width: `${Math.min(b.pct, 100)}%` }}
                  data-testid={`budget-bar-${b.id}`} />
              </div>
              <p className="tnum mt-2 font-mono text-[11px] text-slate-400">%{b.pct} · {t.budget.remaining}: {fmt(b.monthly_limit - b.spent)}</p>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="budget-dialog">
          <DialogHeader><DialogTitle>{editing ? t.budget.edit : t.budget.new}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="space-y-2">
              <Label>{t.app.category}</Label>
              <Select value={form.category || undefined} onValueChange={(v) => setForm({ ...form, category: v })} data-testid="budget-category-select">
                <SelectTrigger><SelectValue placeholder={t.app.category} /></SelectTrigger>
                <SelectContent>{availCats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t.budget.limit}</Label>
              <Input type="number" step="0.01" min="0" value={form.monthly_limit}
                onChange={(e) => setForm({ ...form, monthly_limit: e.target.value })} data-testid="budget-limit-input" />
            </div>
            {error && <p className="rounded-md border border-red-100 bg-red-50 px-3.5 py-2.5 text-sm text-red-600" data-testid="budget-error">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="budget-cancel-button">{t.app.cancel}</Button>
            <Button onClick={save} disabled={!form.category || !form.monthly_limit} data-testid="budget-save-button">{t.app.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
